// authFetch.ts
// Utilitário para fetch autenticado com refresh token automático

// Centralização da URL base da API
function resolveApiBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_API_URL;
  if (fromEnv && String(fromEnv).trim()) {
    return String(fromEnv).replace(/\/$/, '');
  }
  if (typeof window === 'undefined') {
    return 'http://localhost:8000';
  }
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') {
    return 'http://localhost:8000';
  }
  // Admin e site em subdomínios distintos da API
  if (host === 'admin.protecbens.com' || host === 'protecbens.com' || host === 'www.protecbens.com') {
    return 'https://api.protecbens.com';
  }
  return window.location.origin;
}

export const API_BASE_URL = resolveApiBaseUrl();

export async function authFetch(input: RequestInfo, init?: RequestInit): Promise<Response> {
  const token = localStorage.getItem('token');
  const refreshToken = localStorage.getItem('refresh_token');
  let headers = { ...(init?.headers || {}), Authorization: `Bearer ${token}` };
  let url = typeof input === 'string' && input.startsWith('/') ? API_BASE_URL + input : input;
  let response = await fetch(url, { ...init, headers });

  if (response.status === 401 && refreshToken) {
    // Tentar renovar o token
    const refreshResponse = await fetch(`${API_BASE_URL}/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken })
    });
    if (refreshResponse.ok) {
      const { access_token, refresh_token } = await refreshResponse.json();
      localStorage.setItem('token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      // Repetir a requisição original com novo token
      headers = { ...(init?.headers || {}), Authorization: `Bearer ${access_token}` };
      response = await fetch(url, { ...init, headers });
    } else {
      // Falha ao renovar, limpar tokens e redirecionar
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('session_start');
      localStorage.removeItem('userName');
      localStorage.removeItem('userPlan');
      window.dispatchEvent(new Event('token-expirado'));
      setTimeout(() => {
        window.location.href = '/';
      }, 200);
      throw new Error('Sessão expirada. Faça login novamente.');
    }
  }
  return response;
}

interface HistoricoParams {
  tipo?: string;
  data_inicio?: string;
  data_fim?: string;
  pagina?: number;
  limite?: number;
}

export async function fetchHistoricoCompleto({ tipo, data_inicio, data_fim, pagina = 1, limite = 20 }: HistoricoParams = {}) {
  const params = new URLSearchParams();
  if (tipo && tipo !== 'all') params.append('tipo', tipo);
  if (data_inicio) params.append('data_inicio', data_inicio);
  if (data_fim) params.append('data_fim', data_fim);
  params.append('pagina', String(pagina));
  params.append('limite', String(limite));
  const url = `${API_BASE_URL}/historico/completo?${params.toString()}`;
  const response = await authFetch(url);
  if (!response.ok) throw new Error('Erro ao buscar histórico');
  return response.json();
}

// PLACAS DE INTERESSE
export async function fetchPlacasInteresse() {
  const response = await authFetch(`${API_BASE_URL}/placas_interesse`);
  if (!response.ok) throw new Error('Erro ao buscar placas de interesse');
  return response.json();
}

export async function createPlacaInteresse(data: any) {
  const response = await authFetch(`${API_BASE_URL}/placas_interesse`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error('Erro ao criar placa de interesse');
  return response.json();
}

export async function updatePlacaInteresse(id: number, data: any) {
  const response = await authFetch(`${API_BASE_URL}/placas_interesse/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error('Erro ao atualizar placa de interesse');
  return response.json();
}

export async function deletePlacaInteresse(id: number) {
  const response = await authFetch(`${API_BASE_URL}/placas_interesse/${id}`, {
    method: 'DELETE' });
  if (!response.ok) throw new Error('Erro ao deletar placa de interesse');
  return response.json();
}

// PESSOAS DE INTERESSE
export async function fetchPessoasInteresse() {
  const response = await authFetch(`${API_BASE_URL}/pessoas_interesse`);
  if (!response.ok) throw new Error('Erro ao buscar pessoas de interesse');
  return response.json();
}

export async function createPessoaInteresse(data: any) {
  const response = await authFetch(`${API_BASE_URL}/pessoas_interesse`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error('Erro ao criar pessoa de interesse');
  return response.json();
}

export async function updatePessoaInteresse(id: number, data: any) {
  const response = await authFetch(`${API_BASE_URL}/pessoas_interesse/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error('Erro ao atualizar pessoa de interesse');
  return response.json();
}

export async function deletePessoaInteresse(id: number) {
  const response = await authFetch(`${API_BASE_URL}/pessoas_interesse/${id}`, {
    method: 'DELETE' });
  if (!response.ok) throw new Error('Erro ao deletar pessoa de interesse');
  return response.json();
}

export async function buscaFacial(file: File, limite = 20) {
  const token = localStorage.getItem('token');
  const form = new FormData();
  form.append('file', file);
  const controller = new AbortController();
  const timeoutMs = 60000;
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`${API_BASE_URL}/busca-facial?limite=${limite}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
      signal: controller.signal,
    });
    if (!response.ok) {
      let detail = 'Erro na busca facial';
      try {
        const err = await response.json();
        detail = err.detail || detail;
      } catch { /* ignore */ }
      throw new Error(typeof detail === 'string' ? detail : 'Erro na busca facial');
    }
    return response.json();
  } catch (e: unknown) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new Error('Busca facial demorou demais. Verifique se o backend está respondendo e tente de novo.');
    }
    throw e;
  } finally {
    window.clearTimeout(timer);
  }
}


// ========================================
// ADMIN - CÂMERAS
// ========================================
export interface CameraData {
  id?: number;
  user_id?: number;
  nome: string;
  localizacao?: string;
  rtsp_url?: string;
  cloudflare_url?: string;
  web_url?: string;
  
  // Conexão API Intelbras HTTP V3.59
  ip?: string;            // IP da câmera/DVR/NVR
  ip_address?: string;    // Alias para compatibilidade
  porta?: number;         // Porta HTTP (default 80)
  rtsp_porta?: number;    // Porta RTSP (default 554)
  porta_web?: number;     // Porta interface web
  usuario?: string;       // Usuário (default admin)
  senha?: string;         // Senha
  canal?: number;         // Canal de vídeo (1-based)
  
  // Informações do dispositivo
  status?: string;        // online, offline, manutencao
  tipo?: string;          // dome, bullet, ptz, dvr, nvr
  fabricante?: string;    // intelbras, dahua, hikvision
  modelo?: string;        // Modelo do dispositivo
  firmware?: string;      // Versão firmware
  resolucao?: string;     // 1080p, 720p, 4K
  ptz_suportado?: boolean; // Suporta PTZ?
  
  // Localização
  observacao?: string;
  latitude?: number;
  longitude?: number;
  
  // P2P (opcional)
  serial_p2p?: string;    // Serial para conexão P2P
  stream_id?: string;     // ID do stream no go2rtc
  /** JSON: retângulo 0–1 no quadro; só grava detecção se veículo estiver na zona (ver env-backend-example) */
  deteccao_zona_json?: string | null;
  deteccao_linha_json?: string | null;
  face_monitor_enabled?: boolean;
  /** captura = só admin | visualizacao = admin + clientes ao vivo */
  finalidade?: 'captura' | 'visualizacao' | string;
}

// URL base do go2rtc (servidor de streaming)
export const GO2RTC_URL = import.meta.env.VITE_GO2RTC_URL || 'http://localhost:1984';

export async function fetchAdminCameras(): Promise<CameraData[]> {
  const response = await adminFetch(`${API_BASE_URL}/admin/cameras`);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const detail = typeof err.detail === 'string' ? err.detail : `HTTP ${response.status}`;
    throw new Error(detail || 'Erro ao buscar câmeras');
  }
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error(
      'API retornou HTML em vez de JSON. Rebuild do front com VITE_API_URL=https://api.protecbens.com'
    );
  }
  return response.json();
}

export async function fetchUserCameras() {
  const response = await authFetch(`${API_BASE_URL}/api/cameras`);
  if (!response.ok) throw new Error('Erro ao buscar câmeras');
  return response.json();
}

export async function fetchAdminCamera(id: number) {
  const response = await fetch(`${API_BASE_URL}/admin/cameras/${id}`);
  if (!response.ok) throw new Error('Erro ao buscar câmera');
  return response.json();
}

export async function createAdminCamera(data: CameraData) {
  const response = await adminFetch(`${API_BASE_URL}/admin/cameras`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error('Erro ao criar câmera');
  return response.json();
}

export async function updateAdminCamera(id: number, data: Partial<CameraData>) {
  const response = await adminFetch(`${API_BASE_URL}/admin/cameras/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error('Erro ao atualizar câmera');
  return response.json();
}

export async function deleteAdminCamera(id: number) {
  const response = await adminFetch(`${API_BASE_URL}/admin/cameras/${id}`, {
    method: 'DELETE'
  });
  if (!response.ok) throw new Error('Erro ao deletar câmera');
  return response.json();
}

// Permissões de visualização: quais clientes podem ver uma câmera
export interface CameraPermissionItem {
  id: number;
  user_id: number;
  user_nome: string;
  user_email: string;
  created_at: string | null;
}

// Fetch com token de admin (rotas /admin/* exigem admin_token). Exportado para ClientManagement e outros.
export function adminFetch(url: string, init?: RequestInit, timeoutMs = 20000): Promise<Response> {
  const token = localStorage.getItem('admin_token');
  if (!token) {
    return Promise.reject(new Error('Sessão de admin não encontrada. Faça login na área admin.'));
  }
  const headers = { ...(init?.headers || {}), Authorization: `Bearer ${token}` };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...init, headers, signal: controller.signal }).finally(() =>
    clearTimeout(timer)
  );
}

export async function fetchCameraPermissions(cameraId: number): Promise<{ camera_id: number; camera_nome: string; permissions: CameraPermissionItem[] }> {
  const response = await adminFetch(`${API_BASE_URL}/admin/cameras/${cameraId}/permissions`);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Erro ao buscar permissões');
  }
  return response.json();
}

export async function addCameraPermission(cameraId: number, userId: number): Promise<{ message: string; permission_id: number }> {
  const response = await adminFetch(`${API_BASE_URL}/admin/cameras/${cameraId}/permissions?user_id=${userId}`, {
    method: 'POST'
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Erro ao conceder permissão');
  }
  return response.json();
}

export async function removeCameraPermission(cameraId: number, userId: number): Promise<{ message: string }> {
  const response = await adminFetch(`${API_BASE_URL}/admin/cameras/${cameraId}/permissions/${userId}`, {
    method: 'DELETE'
  });
  if (!response.ok) throw new Error('Erro ao revogar permissão');
  return response.json();
}

export interface AdminClientItem {
  id: number;
  nome_completo: string;
  email: string;
  cpf?: string;
  plan?: string;
  status?: string;
  consultas_count?: number;
}

export async function fetchAdminClients(): Promise<AdminClientItem[]> {
  const response = await adminFetch(`${API_BASE_URL}/admin/clients`);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Erro ao buscar clientes');
  }
  const data = await response.json();
  return Array.isArray(data) ? data : data.clients || [];
}

export async function updateCameraStatus(id: number, status: string) {
  const response = await fetch(`${API_BASE_URL}/admin/cameras/${id}/status?status=${status}`, {
    method: 'PUT'
  });
  if (!response.ok) throw new Error('Erro ao alterar status da câmera');
  return response.json();
}


// ========================================
// ADMIN - USUÁRIOS
// ========================================
export interface AdminUserData {
  id?: number;
  nome: string;
  email: string;
  password?: string;
  role: string;
  is_active?: boolean;
}

export async function fetchAdminUsers() {
  const response = await fetch(`${API_BASE_URL}/admin/users`);
  if (!response.ok) throw new Error('Erro ao buscar usuários');
  return response.json();
}

export async function createAdminUser(data: AdminUserData) {
  const response = await fetch(`${API_BASE_URL}/admin/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Erro ao criar usuário');
  }
  return response.json();
}

export async function updateAdminUser(id: number, data: Partial<AdminUserData>) {
  const response = await fetch(`${API_BASE_URL}/admin/users/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error('Erro ao atualizar usuário');
  return response.json();
}

export async function deleteAdminUser(id: number) {
  const response = await fetch(`${API_BASE_URL}/admin/users/${id}`, {
    method: 'DELETE'
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Erro ao deletar usuário');
  }
  return response.json();
}

export async function adminLogin(email: string, password: string) {
  const response = await fetch(`${API_BASE_URL}/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Erro ao fazer login');
  }
  return response.json();
}


// ========================================
// ADMIN - ESTATÍSTICAS
// ========================================
export async function fetchAdminStats() {
  const response = await fetch(`${API_BASE_URL}/admin/stats`);
  if (!response.ok) throw new Error('Erro ao buscar estatísticas');
  return response.json();
}


// ========================================
// ADMIN - VERIFICAÇÃO DE CÂMERAS
// ========================================
export async function verificarStatusCamera(id: number) {
  const response = await fetch(`${API_BASE_URL}/admin/cameras/${id}/verificar-status`, {
    method: 'POST'
  });
  if (!response.ok) throw new Error('Erro ao verificar status');
  return response.json();
}

export async function verificarTodasCameras() {
  const response = await fetch(`${API_BASE_URL}/admin/cameras/verificar-todas`, {
    method: 'POST'
  });
  if (!response.ok) throw new Error('Erro ao verificar câmeras');
  return response.json();
}


// ========================================
// ADMIN - EVENTOS DE CÂMERA
// ========================================
export interface CameraEvent {
  id: number;
  camera_id: number;
  tipo: string;
  descricao?: string;
  placa?: string;
  imagem_url?: string;
  confianca?: number;
  data_hora: string;
  processado: boolean;
}

export async function fetchCameraEvents(cameraId: number, tipo?: string, limite: number = 50) {
  const params = new URLSearchParams();
  if (tipo) params.append('tipo', tipo);
  params.append('limite', String(limite));
  
  const response = await fetch(`${API_BASE_URL}/cameras/${cameraId}/eventos?${params.toString()}`);
  if (!response.ok) throw new Error('Erro ao buscar eventos');
  return response.json();
}

export async function fetchAllEvents(tipo?: string, cameraId?: number, limite: number = 100) {
  const params = new URLSearchParams();
  if (tipo) params.append('tipo', tipo);
  if (cameraId) params.append('camera_id', String(cameraId));
  params.append('limite', String(limite));
  
  const response = await fetch(`${API_BASE_URL}/admin/eventos?${params.toString()}`);
  if (!response.ok) throw new Error('Erro ao buscar eventos');
  return response.json();
}

export async function fetchUltimasDeteccoes(limite: number = 10) {
  const response = await fetch(`${API_BASE_URL}/admin/eventos/ultimas-deteccoes?limite=${limite}`);
  if (!response.ok) throw new Error('Erro ao buscar detecções');
  return response.json();
}


// ========================================
// ADMIN - LOGS DO SISTEMA
// ========================================
export interface SystemLog {
  id: number;
  tipo: string;
  categoria: string;
  mensagem: string;
  detalhes?: string;
  ip_address?: string;
  user_id?: number;
  admin_user_id?: number;
  data_hora: string;
}

export async function fetchSystemLogs(tipo?: string, categoria?: string, limite: number = 100) {
  const params = new URLSearchParams();
  if (tipo) params.append('tipo', tipo);
  if (categoria) params.append('categoria', categoria);
  params.append('limite', String(limite));
  
  const response = await fetch(`${API_BASE_URL}/admin/logs?${params.toString()}`);
  if (!response.ok) throw new Error('Erro ao buscar logs');
  return response.json();
}

export async function fetchRecentLogs(limite: number = 20) {
  const response = await fetch(`${API_BASE_URL}/admin/logs/recentes?limite=${limite}`);
  if (!response.ok) throw new Error('Erro ao buscar logs recentes');
  return response.json();
}